import { useContext, useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  FileText,
  Receipt,
  Users,
  Globe,
  LogOut,
  Menu,
  X,
  Calculator,
  ClipboardList,
  Wallet,
  Building2,
  PieChart,
  CreditCard,
  Landmark,
  DollarSign,
  MessageCircle,
  User,
  HandCoins,
  ArrowRightLeft,
  Sun,
  Moon
} from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';

export default function Layout({ children }) {
  const { user, logout, token } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appTheme, setAppTheme] = useState(() => localStorage.getItem('app-theme') || 'dark');
  const isDark = appTheme === 'dark';

  // Sync dark-theme class on <html>
  useEffect(() => {
    if (appTheme === 'dark') {
      document.documentElement.classList.add('dark-theme');
    } else {
      document.documentElement.classList.remove('dark-theme');
    }
    localStorage.setItem('app-theme', appTheme);
  }, [appTheme]);

  const toggleAppTheme = () => setAppTheme(prev => prev === 'dark' ? 'light' : 'dark');

  const [pendingCounts, setPendingCounts] = useState({
    orders: 0,
    posOrders: 0,
    rfqOrders: 0,
    quotations: 0,
    invoices: 0,
    creditInvoices: 0,
    unseenCustomers: 0,
    pendingTransfers: 0
  });

  // TOTP OTP Gate
  const OTP_SESSION_KEY = 'otp_verified_until';
  const OTP_VALIDITY_MINUTES = 5;
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [protectedPaths, setProtectedPaths] = useState([]);
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpTarget, setOtpTarget] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const isOtpValid = useCallback(() => {
    const until = sessionStorage.getItem(OTP_SESSION_KEY);
    if (!until) return false;
    return new Date(until) > new Date();
  }, []);

  useEffect(() => {
    const checkTotp = async () => {
      try {
        const res = await axios.get(`${API}/settings/totp/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTotpEnabled(res.data.enabled);
        setProtectedPaths(res.data.protected_pages || []);
      } catch {}
    };
    checkTotp();
  }, [token]);

  const handleProtectedNav = (e, path) => {
    if (!totpEnabled || !protectedPaths.includes(path) || isOtpValid()) return;
    e.preventDefault();
    setOtpTarget(path);
    setOtpCode('');
    setShowOtpDialog(true);
  };

  const handleOtpSubmit = async () => {
    if (!otpCode || otpCode.length < 6) return;
    setOtpLoading(true);
    try {
      await axios.post(`${API}/settings/totp/verify`, { code: otpCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Cache verification
      const until = new Date();
      until.setMinutes(until.getMinutes() + OTP_VALIDITY_MINUTES);
      sessionStorage.setItem(OTP_SESSION_KEY, until.toISOString());
      setShowOtpDialog(false);
      setOtpCode('');
      toast.success('Access granted');
      navigate(otpTarget);
    } catch {
      toast.error('Invalid OTP code');
    } finally {
      setOtpLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingCounts();
  }, []);

  const fetchPendingCounts = async () => {
    try {
      // Fetch POS orders (pending)
      const ordersRes = await axios.get(`${API}/orders?status=new`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const processingOrdersRes = await axios.get(`${API}/orders?status=processing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const posOrdersCount = ordersRes.data.length + processingOrdersRes.data.length;
      
      // Fetch RFQ/Customer orders (pending)
      const customerOrdersRes = await axios.get(`${API}/admin/customer-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rfqOrdersCount = customerOrdersRes.data.filter(order => 
        order.status === 'Open' || order.status === 'Processing'
      ).length;
      
      // Fetch pending quotations
      const quotationsRes = await axios.get(`${API}/quotations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pendingQuotations = quotationsRes.data.filter(q => q.status !== 'converted' && q.status !== 'expired');
      
      // Fetch unpaid invoices
      const invoicesRes = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const unpaidInvoices = invoicesRes.data.filter(inv => inv.status === 'unpaid');
      
      // Fetch credit invoices pending verification
      let pendingCreditInvoicesCount = 0;
      try {
        const creditInvoicesRes = await axios.get(`${API}/invoices/credit`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Credit invoices:', creditInvoicesRes.data);
        const pendingCreditInvoices = creditInvoicesRes.data.filter(inv => inv.payment_status === 'verifying');
        console.log('Pending credit invoices (verifying):', pendingCreditInvoices);
        pendingCreditInvoicesCount = pendingCreditInvoices.length;
      } catch (error) {
        console.error('Failed to fetch credit invoices:', error);
        // Continue without credit invoices count
      }

      // Fetch unseen customers count
      let unseenCustomersCount = 0;
      try {
        const unseenRes = await axios.get(`${API}/customers/unseen-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        unseenCustomersCount = unseenRes.data.count || 0;
      } catch (error) {
        console.error('Failed to fetch unseen customers count');
      }

      // Fetch pending transfers awaiting approval (exclude own requests)
      let pendingTransfersCount = 0;
      try {
        const transfersRes = await axios.get(`${API}/accounting/transfers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        pendingTransfersCount = transfersRes.data.filter(t =>
          t.status === 'pending' && t.requested_by !== user?.email
        ).length;
      } catch (error) {
        console.error('Failed to fetch pending transfers count');
      }
      
      const counts = {
        orders: posOrdersCount + rfqOrdersCount,
        posOrders: posOrdersCount,
        rfqOrders: rfqOrdersCount,
        quotations: pendingQuotations.length,
        invoices: unpaidInvoices.length,
        creditInvoices: pendingCreditInvoicesCount,
        unseenCustomers: unseenCustomersCount,
        pendingTransfers: pendingTransfersCount
      };
      console.log('Setting pending counts:', counts);
      setPendingCounts(counts);
    } catch (error) {
      console.error('Failed to fetch pending counts');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'dashboard', glow: '#10b981' },
    { path: '/pos', label: 'POS', icon: ShoppingCart, permission: 'pos', glow: '#06b6d4' },
    { path: '/orders', label: 'Orders', icon: ClipboardList, permission: 'orders', glow: '#f59e0b' },
    { path: '/sales-history', label: 'Sales History', icon: Receipt, permission: 'sales_history', glow: '#ec4899' },
    { path: '/inventory', label: 'Inventory', icon: Package, permission: 'inventory', glow: '#8b5cf6' },
    { path: '/quotations', label: 'Quotations', icon: FileText, permission: 'quotations', glow: '#f97316' },
    { path: '/invoices', label: 'Invoices', icon: Receipt, permission: 'invoices', glow: '#ef4444' },
    { path: '/credit-invoices', label: 'Credit Invoices', icon: CreditCard, permission: 'credit_invoices', glow: '#a855f7' },
    { path: '/costing', label: 'Costing', icon: Calculator, permission: 'costing', glow: '#14b8a6' },
    { path: '/accounting', label: 'Accounting', icon: Wallet, permission: 'accounting', glow: '#3b82f6' },
    { path: '/expenses', label: 'Expenses', icon: Receipt, permission: 'expenses', glow: '#f43f5e' },
    { path: '/purchase-orders', label: 'Purchase Orders', icon: ShoppingCart, permission: 'purchase_orders', glow: '#22d3ee' },
    { path: '/suppliers', label: 'Suppliers', icon: Building2, permission: 'suppliers', glow: '#6366f1' },
    { path: '/budget', label: 'Budget', icon: PieChart, permission: 'budget', glow: '#84cc16' },
    { path: '/tax', label: 'Tax', icon: Landmark, permission: 'tax', glow: '#eab308' },
    { path: '/petty-cash', label: 'Petty Cash', icon: DollarSign, permission: 'pettycash_create', glow: '#34d399' },
    { path: '/loans', label: 'Loans', icon: HandCoins, permission: 'loans', glow: '#fb923c' },
    { path: '/transfers', label: 'Transfers', icon: ArrowRightLeft, permission: 'transfers', glow: '#818cf8' },
    { path: '/reports', label: 'Reports', icon: FileText, permission: 'reports', glow: '#2dd4bf' },
    { path: '/users', label: 'Users', icon: Users, permission: 'users', glow: '#60a5fa' },
    { path: '/customers', label: 'Customers', icon: Users, permission: 'admin_only', glow: '#4ade80' },
    { path: '/website-cms', label: 'Website', icon: Globe, permission: 'website', glow: '#c084fc' },
    { path: '/settings', label: 'Settings', icon: Users, permission: 'settings', glow: '#94a3b8' },
  ];

  const hasPermission = (permission) => {
    if (!user) return false;
    
    // For customers, only allow dashboard access (for now)
    // Later when customer portal is built, add more permissions
    if (user.user_type === 'customer') {
      return permission === 'dashboard';
    }
    
    // Dashboard is visible to all non-customer users
    if (permission === 'dashboard') return true;
    
    // Special permission for super admin only
    if (permission === 'super_admin_only') {
      return user.role === 'Super Admin';
    }
    
    // Permission for both Admin and Super Admin
    if (permission === 'admin_only') {
      return user.role === 'Super Admin' || user.role === 'Admin';
    }
    
    // For staff users
    if (!user.permissions) return false;
    if (user.permissions.includes('all')) return true;
    return user.permissions.includes(permission);
  };

  const filteredNavItems = navItems.filter(item => hasPermission(item.permission));

  return (
    <div className={`flex h-screen ${isDark ? 'bg-slate-950' : 'bg-slate-50'}`}>
      {/* Sidebar */}
      <aside className={`group/sidebar fixed inset-y-0 left-0 z-50 w-64 lg:w-16 lg:hover:w-64 transform transition-all duration-300 overflow-hidden lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Mobile: solid dark bg. Desktop: transparent, glass on hover */}
        <div className="absolute inset-0 bg-slate-900/95 backdrop-blur-xl lg:bg-transparent lg:backdrop-blur-none lg:group-hover/sidebar:bg-black/50 lg:group-hover/sidebar:backdrop-blur-xl transition-colors duration-300 pointer-events-none" />
        <div className="relative flex flex-col h-full z-10">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 lg:px-0 lg:justify-center lg:group-hover/sidebar:px-6 lg:group-hover/sidebar:justify-between border-b border-white/5 transition-all duration-300">
            <h1 className="text-xl font-bold text-emerald-400 whitespace-nowrap lg:hidden lg:group-hover/sidebar:block" style={{ filter: 'drop-shadow(0 0 8px #10b98180)' }}>ASSETCO</h1>
            <span className="hidden lg:block lg:group-hover/sidebar:hidden text-xl font-bold text-emerald-400" style={{ filter: 'drop-shadow(0 0 8px #10b98180)' }}>A</span>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 lg:px-2 lg:group-hover/sidebar:px-4 py-6 space-y-1 overflow-y-auto transition-all duration-300">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              // Get pending count for this item
              let pendingCount = 0;
              if (item.label === 'Orders') pendingCount = pendingCounts.orders;
              if (item.label === 'Quotations') pendingCount = pendingCounts.quotations;
              if (item.label === 'Invoices') pendingCount = pendingCounts.invoices;
              if (item.label === 'Credit Invoices') pendingCount = pendingCounts.creditInvoices;
              if (item.label === 'Customers') pendingCount = pendingCounts.unseenCustomers;
              if (item.label === 'Transfers') pendingCount = pendingCounts.pendingTransfers;
              
              return (
                <Link
                  key={item.path}
                  to={protectedPaths.includes(item.path) && totpEnabled && !isOtpValid() ? '#' : item.path}
                  onClick={(e) => {
                    if (protectedPaths.includes(item.path)) {
                      handleProtectedNav(e, item.path);
                    }
                    setSidebarOpen(false);
                  }}
                  className={`flex items-center justify-between gap-3 px-4 lg:px-3 lg:group-hover/sidebar:px-4 py-3 rounded-lg font-medium transition-all ${
                    isActive
                      ? 'bg-white/10'
                      : 'hover:bg-white/5'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon
                      className="w-5 h-5 shrink-0"
                      style={{
                        color: item.glow,
                        filter: `drop-shadow(0 0 6px ${item.glow}) drop-shadow(0 0 12px ${item.glow}60)`,
                      }}
                    />
                    <span className={`whitespace-nowrap lg:opacity-0 lg:group-hover/sidebar:opacity-100 transition-opacity duration-200 ${isActive ? 'text-white' : 'text-slate-300'}`}>{item.label}</span>
                  </div>
                  {pendingCount > 0 && (
                    <span className="px-2 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full shrink-0 lg:opacity-0 lg:group-hover/sidebar:opacity-100 transition-opacity duration-200">
                      {pendingCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 lg:px-2 lg:group-hover/sidebar:px-4 border-t border-white/5 transition-all duration-300">
            <div className="flex items-center gap-3 mb-3 lg:justify-center lg:group-hover/sidebar:justify-start">
              <div className="w-10 h-10 shrink-0 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-semibold" style={{ boxShadow: '0 0 12px #10b98160' }}>
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0 lg:hidden lg:group-hover/sidebar:block">
                <p className="text-sm font-semibold text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.role}</p>
              </div>
            </div>
            <div className="flex gap-2 lg:hidden lg:group-hover/sidebar:flex">
              <Link to="/profile" className="flex-1" onClick={() => setSidebarOpen(false)}>
                <Button
                  variant="outline"
                  className="w-full border-white/20 text-slate-300 hover:bg-white/10 hover:text-white bg-transparent"
                  data-testid="manage-profile-button"
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </Button>
              </Link>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-white/20 text-slate-300 hover:bg-white/10 hover:text-white bg-transparent"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:ml-16">
        {/* Top bar: mobile hamburger + theme toggle + notification bell */}
        <header className={`h-14 flex items-center justify-between px-4 ${isDark ? 'bg-slate-900/90 backdrop-blur-xl lg:bg-transparent lg:backdrop-blur-none' : 'bg-white/80 backdrop-blur-xl lg:bg-transparent lg:backdrop-blur-none border-b border-slate-200 lg:border-transparent'}`}>
          <button
            onClick={() => setSidebarOpen(true)}
            className={`lg:hidden ${isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className={`text-lg font-bold lg:hidden ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} style={isDark ? { filter: 'drop-shadow(0 0 6px #10b98160)' } : {}}>ASSETCO</h1>
          {/* Spacer on desktop */}
          <div className="hidden lg:block" />
          <div className="flex items-center gap-1">
            {/* Theme toggle */}
            <button
              onClick={toggleAppTheme}
              className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-slate-300' : 'hover:bg-slate-100 text-slate-500'}`}
              data-testid="theme-toggle-button"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <NotificationBell isDark={isDark} />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        />
      )}

      {/* OTP Verification Dialog */}
      <Dialog open={showOtpDialog} onOpenChange={setShowOtpDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Landmark className="w-5 h-5 text-emerald-600" />
              OTP Verification Required
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Enter the 6-digit code from your Google Authenticator app to access this section.
            </p>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleOtpSubmit()}
              className="text-center text-2xl tracking-[0.5em] font-mono h-14"
              autoFocus
              data-testid="otp-input"
            />
            <Button
              onClick={handleOtpSubmit}
              disabled={otpLoading || otpCode.length < 6}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="otp-submit"
            >
              {otpLoading ? 'Verifying...' : 'Verify & Access'}
            </Button>
            <p className="text-xs text-slate-400 text-center">
              Access is cached for {OTP_VALIDITY_MINUTES} minutes after verification
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}